import { requestJson, responseArray, responseRecord } from './http';

export const CONTENT_CLASSES = ['documents', 'images', 'video', 'audio', 'exports'] as const;
export type ContentClass = (typeof CONTENT_CLASSES)[number];
export type StorageLocationKind = 'cloud_object' | 'node_local' | 'node_nas';

export interface StorageLocation {
  location_id: string;
  kind: StorageLocationKind;
  label: string;
  device_id: string | null;
  root_alias: string | null;
  built_in: boolean;
  availability: { state: 'available' | 'unverified' | 'unavailable'; reason: string };
  created_at: string | null;
}

export interface EffectiveStoragePreference {
  location_id: string | null;
  kind?: StorageLocationKind;
  device_id?: string | null;
  source: 'project' | 'org' | 'derived_default';
  availability?: { state: 'available' | 'unverified' | 'unavailable'; reason: string };
  requires_location?: boolean;
}

export interface StoragePreferences {
  scope: 'org' | 'project';
  project_id: string | null;
  defaults_confirmed_at: string | null;
  org_defaults_confirmed_at: string | null;
  preferences: Partial<Record<ContentClass, EffectiveStoragePreference>>;
  derived_defaults: Partial<Record<ContentClass, {
    kind: StorageLocationKind;
    location_id: string | null;
    device_id: string | null;
    requires_location: boolean;
    basis: string;
  }>>;
  moves_existing_files: false;
}

export interface StorageRepresentation {
  representation_id: string;
  asset_id: string;
  project_id: string;
  location_id: string;
  kind: string;
  uri: string;
  file_name: string;
  availability: string;
  checksum: string | null;
  verified_at: string | null;
  is_primary: boolean;
  size_bytes: number | null;
  metadata: Record<string, unknown>;
  retired_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface RetireConfirmation {
  binding: {
    asset_id: string;
    source_representation_id: string;
    destination_representation_id: string;
    checksum: string;
    operation_version: number;
  };
  approval_required: boolean;
  approval_operation: string | null;
  approval_asset_refs: string[] | null;
}

export interface StorageOperation {
  operation_id: string;
  project_id: string;
  op: 'link' | 'import' | 'mirror' | 'move' | 'reconcile';
  state: string;
  version: number;
  executor: 'cloud' | 'node';
  device_id: string | null;
  asset_id: string | null;
  source_representation_id: string | null;
  source_location_id: string | null;
  destination_location_id: string | null;
  destination_representation_id: string | null;
  expected_checksum: string | null;
  failure_code: string | null;
  awaiting: 'node_result' | 'confirm_retire' | null;
  node_job: { operation: string; job_ref: string } | null;
  retire_confirmation: RetireConfirmation | null;
  created_at: string;
  updated_at: string;
  finished_at: string | null;
}

export interface StorageOperationReceipt {
  receipt_id: string;
  sequence: number;
  step: string;
  to_state: string;
  recorded_at: string | null;
}

export interface StorageAssetChoice {
  asset_id: string;
  file_name: string;
  copy_count: number;
}

export type StorageOperationInput =
  | {
      op: 'link';
      idempotency_key: string;
      location_id: string;
      relative_key: string;
      asset_id?: string;
      origin_key?: string;
      checksum?: string;
      kind?: string;
      approval_receipt_id?: string;
    }
  | {
      op: 'import' | 'mirror' | 'move';
      idempotency_key: string;
      asset_id: string;
      source_representation_id?: string;
      destination_location_id: string;
      destination_relative_key?: string;
      content_class?: ContentClass;
      approval_receipt_id?: string;
    };

export async function listStorageLocations(): Promise<StorageLocation[]> {
  const payload = await requestJson('/v1/storage/locations');
  return responseArray(payload, 'locations') as StorageLocation[];
}

export async function createStorageLocation(input: {
  kind: 'node_local' | 'node_nas';
  device_id: string;
  root_alias: string;
  label?: string;
}): Promise<StorageLocation> {
  const payload = await requestJson('/v1/storage/locations', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return responseRecord(payload, 'location') as unknown as StorageLocation;
}

export async function deleteStorageLocation(locationId: string): Promise<{ location_id: string; deleted_at: string }> {
  const payload = await requestJson('/v1/storage/locations/' + encodeURIComponent(locationId), {
    method: 'DELETE',
  });
  return responseRecord(payload, 'deleted') as unknown as { location_id: string; deleted_at: string };
}

export async function getStoragePreferences(projectId?: string): Promise<StoragePreferences> {
  const query = projectId ? '?project_id=' + encodeURIComponent(projectId) : '';
  const payload = await requestJson('/v1/storage/preferences' + query);
  return responseRecord(payload, 'storage_preferences') as unknown as StoragePreferences;
}

export async function putStoragePreferences(input: {
  projectId?: string;
  values: Partial<Record<ContentClass, string | null>>;
  confirmDefaults?: boolean;
}): Promise<StoragePreferences> {
  const body: Record<string, unknown> = { ...input.values };
  if (input.projectId) body.project_id = input.projectId;
  if (input.confirmDefaults) body.confirm_defaults = true;
  const payload = await requestJson('/v1/storage/preferences', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  return responseRecord(payload, 'storage_preferences') as unknown as StoragePreferences;
}

export async function listAssetRepresentations(
  projectId: string,
  assetId: string,
): Promise<StorageRepresentation[]> {
  const payload = await requestJson(
    '/v1/projects/' + encodeURIComponent(projectId) + '/assets/' + encodeURIComponent(assetId) + '/representations',
  );
  return responseArray(payload, 'representations') as StorageRepresentation[];
}

export async function listStorageAssets(projectId: string, query = ''): Promise<StorageAssetChoice[]> {
  const path = '/v1/projects/' + encodeURIComponent(projectId) + '/storage/assets';
  const payload = await requestJson(path + (query.trim() ? '?q=' + encodeURIComponent(query.trim()) : ''));
  return responseArray(payload, 'assets') as StorageAssetChoice[];
}

export async function createStorageOperation(
  projectId: string,
  input: StorageOperationInput,
): Promise<{ operation: StorageOperation; duplicate: boolean }> {
  const payload = await requestJson(
    '/v1/projects/' + encodeURIComponent(projectId) + '/storage/operations',
    { method: 'POST', body: JSON.stringify(input) },
  );
  const record = responseRecord(payload);
  return {
    operation: responseRecord(record, 'operation') as unknown as StorageOperation,
    duplicate: record.duplicate === true,
  };
}

export async function getStorageOperation(
  projectId: string,
  operationId: string,
): Promise<StorageOperation> {
  const payload = await requestJson(
    '/v1/projects/' + encodeURIComponent(projectId) + '/storage/operations/' + encodeURIComponent(operationId),
  );
  return responseRecord(payload, 'operation') as unknown as StorageOperation;
}

export async function getStorageOperationDetail(
  projectId: string,
  operationId: string,
): Promise<{ operation: StorageOperation; receipts: StorageOperationReceipt[] }> {
  const payload = await requestJson(
    '/v1/projects/' + encodeURIComponent(projectId) + '/storage/operations/' + encodeURIComponent(operationId),
  );
  return {
    operation: responseRecord(payload, 'operation') as unknown as StorageOperation,
    receipts: responseArray(payload, 'receipts') as StorageOperationReceipt[],
  };
}

export async function reconcileStorageLocation(
  projectId: string,
  locationId: string,
  reason: 'reconcile' | 'reroot',
): Promise<StorageOperation> {
  const payload = await requestJson('/v1/projects/' + encodeURIComponent(projectId) + '/storage/reconcile', {
    method: 'POST',
    body: JSON.stringify({
      location_id: locationId,
      reason,
      idempotency_key: Date.now().toString(36) + '-' + Math.random().toString(36).slice(2),
    }),
  });
  return responseRecord(payload, 'operation') as unknown as StorageOperation;
}

export async function issueRetireApproval(
  projectId: string,
  operationId: string,
  binding: RetireConfirmation['binding'],
  reason: string,
): Promise<{ approval_receipt_id: string; expires_at: string }> {
  const payload = await requestJson(
    '/v1/projects/' + encodeURIComponent(projectId) + '/storage/operations/' + encodeURIComponent(operationId) + '/retire-approval',
    { method: 'POST', body: JSON.stringify({ binding, reason }) },
  );
  const result = responseRecord(payload);
  return { approval_receipt_id: String(result.approval_receipt_id), expires_at: String(result.expires_at) };
}

export async function actOnStorageOperation(
  projectId: string,
  operationId: string,
  action: 'cancel' | 'confirm_retire',
  binding?: RetireConfirmation['binding'],
  approvalReceiptId?: string,
): Promise<StorageOperation> {
  const body: Record<string, unknown> = { action };
  if (action === 'confirm_retire' && binding) body.binding = binding;
  if (action === 'confirm_retire' && approvalReceiptId) body.approval_receipt_id = approvalReceiptId;
  const payload = await requestJson(
    '/v1/projects/' + encodeURIComponent(projectId) + '/storage/operations/' + encodeURIComponent(operationId),
    { method: 'POST', body: JSON.stringify(body) },
  );
  return responseRecord(payload, 'operation') as unknown as StorageOperation;
}
